// The engagement entity a Community tab shows. Filter and resolve controls
// label themselves with it so the Mentions tab never claims to act on comments.
export type CommunityEntity = 'comments' | 'mentions'

export type FilterStatus = 'unreplied' | 'replied' | 'dismissed' | 'new' | 'all'
export type SortDirection = 'asc' | 'desc'
export type SortOption = 'unansweredFirst' | 'newest' | 'oldest'
export type DateRange = 'last7days' | 'last30days' | 'last6months' | 'lastyear'

export type BaseCommunityFilters = {
  status?: FilterStatus
  sortOption?: SortOption
  /**
   * Relative date-range filter. Consumers that need concrete timestamps
   * (GraphQL query variables) resolve it with `getPublishedAtRange` at the
   * point of use — concrete dates are intentionally not part of filter state.
   */
  publishedAt?: DateRange
  postId?: string
}

export type Filters = BaseCommunityFilters & {
  selectedChannelIds?: string[]
}

/**
 * A partial set of filter changes applied in a single navigation by
 * `setFilters`. A key that is omitted stays untouched; a key passed as
 * `undefined` removes the corresponding URL param.
 */
export type FilterUpdates = {
  status?: FilterStatus
  sortOption?: SortOption
  publishedAt?: DateRange
  selectedChannelIds?: string[]
}

export type UseFiltersReturn = {
  // Current filter values
  filters: Filters
  // Actions to modify filters
  actions: {
    setStatus: (status: FilterStatus) => void
    setSortOption: (option: SortOption | undefined) => void
    setPublishedAt: (publishedAt: DateRange | undefined) => void
    setSelectedChannelIds: (channelIds: string[]) => void
    setFilters: (updates: FilterUpdates) => void
  }
  // Utility functions
  utils: {
    clearFilters: () => void
    hasActiveFilters: boolean
  }
}

export const DATE_RANGES: Record<DateRange, number> = {
  last7days: 7,
  last30days: 30,
  last6months: 6 * 30,
  lastyear: 365,
}

export const isDateRange = (value: string): value is DateRange =>
  Object.keys(DATE_RANGES).includes(value)

export const STATUS_LABELS: Record<FilterStatus, string> = {
  unreplied: 'Unanswered',
  replied: 'Replied',
  dismissed: 'Resolved',
  new: 'Unanswered',
  all: 'All',
}

export const SORT_DIRECTION_LABELS: Record<SortDirection, string> = {
  asc: 'Oldest',
  desc: 'Newest',
}

export const SORT_OPTION_LABELS: Record<SortOption, string> = {
  unansweredFirst: 'Unanswered first',
  newest: 'Newest',
  oldest: 'Oldest',
}

export const DEFAULT_SORT_OPTION: SortOption = 'unansweredFirst'

/**
 * The inbox never defaults to a window wider than what is retained. Absent
 * `publishedAt` resolves to this range.
 */
export const DEFAULT_DATE_RANGE: DateRange = 'lastyear'

export const DEFAULT_STATUS = 'all'
export type DismissActionType = 'all' | 'older-than-month' | 'post'
